/**
 * Dreamy Codes — Cookie Box cart reconciliation
 * -----------------------------------------------------------------------------
 * A cookie box is priced `base + unit x (qty - min)`, but Shopify prices every
 * cart line as `unit price x quantity`. The flat component therefore rides on a
 * second, fixed line:
 *
 *   line 1   cookies, qty = cookie count, priced at `unit`
 *   line 2   box fee,  qty = 1,           priced at `flat`
 *
 * This file keeps line 2 correct no matter what the customer does to line 1.
 * The cart is customer-editable, so these invariants are enforced on every
 * cart event rather than assumed:
 *
 *   - cookies present            -> exactly ONE fee line, at qty 1
 *   - cookies removed            -> fee line removed
 *   - cookie qty out of range    -> clamped back into [min, max]
 *   - repeated add-to-cart       -> never a duplicate fee line
 *
 * Integrates through the theme's documented event API (assets/theme-events.js),
 * not by patching the compiled bundles.
 *
 * Config is emitted by layout/theme.liquid as `window.dcCookieBox`.
 */
(function () {
  'use strict';

  var config = window.dcCookieBox;
  if (!config || !config.boxes || !Object.keys(config.boxes).length) return;

  var routes = (window.theme && window.theme.routes) || {};
  var URL_CART = (routes.cart_url || '/cart') + '.js';
  var URL_ADD = (routes.cart_add_url || '/cart/add') + '.js';
  var URL_CHANGE = (routes.cart_change_url || '/cart/change') + '.js';

  var HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  /**
   * Our own cart writes cause the theme to re-render, which can re-fire the very
   * events this module listens to. Without this guard the reconciler feeds
   * itself and loops forever. Overlapping requests are coalesced into a single
   * follow-up pass rather than dropped, so nothing is silently missed.
   */
  var running = false;
  var queued = false;
  // A coalesced run must inherit any pending request to open the drawer,
  // otherwise an add-to-cart that lands mid-reconcile silently loses it.
  var queuedOpenDrawer = false;

  function boxFor(variantId) {
    return config.boxes[String(variantId)] || null;
  }

  function allFeeIds() {
    return Object.keys(config.boxes).map(function (key) {
      return String(config.boxes[key].feeVariantId);
    });
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getCart() {
    return fetch(URL_CART, {
      headers: HEADERS,
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart read failed: ' + res.status);
      return res.json();
    });
  }

  function changeLine(key, quantity) {
    return fetch(URL_CHANGE, {
      method: 'POST',
      headers: HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart change failed: ' + res.status);
      return res.json();
    });
  }

  function addLine(variantId, quantity) {
    return fetch(URL_ADD, {
      method: 'POST',
      headers: HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: quantity }],
      }),
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);
      return res.json();
    });
  }

  /**
   * Pass 1 — clamp every cookie line into its configured range.
   * Returns true if anything was written.
   */
  function clampCookieLines(cart) {
    var work = Promise.resolve(false);

    cart.items.forEach(function (line) {
      var box = boxFor(line.variant_id);
      if (!box) return;

      var wanted = clamp(line.quantity, box.minQty, box.maxQty);
      if (wanted === line.quantity) return;

      work = work.then(function (changed) {
        return changeLine(line.key, wanted).then(function () {
          return true;
        });
      });
    });

    return work;
  }

  /**
   * Pass 2 — reconcile fee lines against the cookie lines that actually remain.
   * Runs against a freshly read cart so pass 1's edits are accounted for.
   */
  function reconcileFeeLines(cart) {
    var cookieQtyByFee = {};
    var feeLinesByVariant = {};
    var feeIds = allFeeIds();

    cart.items.forEach(function (line) {
      var box = boxFor(line.variant_id);
      if (box) {
        var feeId = String(box.feeVariantId);
        cookieQtyByFee[feeId] = (cookieQtyByFee[feeId] || 0) + line.quantity;
      }

      var variantId = String(line.variant_id);
      if (feeIds.indexOf(variantId) !== -1) {
        if (!feeLinesByVariant[variantId]) feeLinesByVariant[variantId] = [];
        feeLinesByVariant[variantId].push(line);
      }
    });

    var work = Promise.resolve(false);

    feeIds.forEach(function (feeId) {
      var lines = feeLinesByVariant[feeId] || [];
      var wanted = cookieQtyByFee[feeId] ? 1 : 0;

      // No cookies left for this box — drop every fee line.
      if (!wanted) {
        lines.forEach(function (line) {
          work = work.then(function () {
            return changeLine(line.key, 0).then(function () {
              return true;
            });
          });
        });
        return;
      }

      // Cookies present but no fee line — add one.
      if (!lines.length) {
        work = work.then(function () {
          return addLine(feeId, 1).then(function () {
            return true;
          });
        });
        return;
      }

      // Keep the first fee line, remove any duplicates, force it to qty 1.
      lines.slice(1).forEach(function (line) {
        work = work.then(function () {
          return changeLine(line.key, 0).then(function () {
            return true;
          });
        });
      });

      if (lines[0].quantity !== 1) {
        work = work.then(function () {
          return changeLine(lines[0].key, 1).then(function () {
            return true;
          });
        });
      }
    });

    return work;
  }

  /**
   * Open the cart modal.
   *
   * Deliberately NOT via the theme's `theme:open:cart-drawer` event: that
   * handler gates on `window.theme.settings.cart_type`, but js-bridge.liquid
   * emits `cartType`, so the condition never matches and the event is dead in
   * Shapes v4.3.0. We call the same Alpine store the theme itself uses, with
   * the correct key.
   */
  function openCartDrawer() {
    var alpine = window.Alpine;
    if (!alpine || typeof alpine.store !== 'function') return;

    var settings = (window.theme && window.theme.settings) || {};
    if (settings.cartType !== 'modal') return;

    var modals = alpine.store('modals');
    if (!modals || typeof modals.isRegistered !== 'function') return;
    if (!modals.isRegistered('cart')) return;

    modals.closeAll();
    modals.open('cart');
  }

  /**
   * The theme adds the cookie line, then we add the fee line. Opening the
   * drawer any earlier shows a cart that is missing its fee line and a total
   * that is about to change. When we wrote to the cart, wait for the theme to
   * finish re-rendering its sections before opening.
   */
  function openCartDrawerWhenReady(didWrite) {
    if (!didWrite) {
      openCartDrawer();
      return;
    }

    var opened = false;
    var onUpdated = function () {
      if (opened) return;
      opened = true;
      document.removeEventListener('shapes:cart:update', onUpdated);
      openCartDrawer();
    };

    document.addEventListener('shapes:cart:update', onUpdated);
    // Fallback so a missed/renamed event can never leave the drawer shut.
    setTimeout(onUpdated, 1200);
  }

  function reconcile(opts) {
    var wantDrawer = Boolean(opts && opts.openDrawer);

    if (running) {
      queued = true;
      if (wantDrawer) queuedOpenDrawer = true;
      return Promise.resolve();
    }
    running = true;

    var didWrite = false;
    var sawBoxLine = false;

    return getCart()
      .then(function (cart) {
        // Only a cookie-box add should pop the drawer; other products keep
        // whatever behaviour the theme setting gives them.
        cart.items.forEach(function (line) {
          if (boxFor(line.variant_id)) sawBoxLine = true;
        });
        return clampCookieLines(cart);
      })
      .then(function (clamped) {
        if (clamped) didWrite = true;
        // Re-read so pass 2 sees the clamped quantities, not the stale ones.
        return getCart();
      })
      .then(function (cart) {
        return reconcileFeeLines(cart);
      })
      .then(function (synced) {
        if (synced) didWrite = true;
      })
      .catch(function (error) {
        // Never leave the cart mid-reconcile without telling anyone.
        console.error('[dc-cookie-box] Cart reconciliation failed:', error);
      })
      .then(function () {
        running = false;

        // Let the theme re-render its own cart sections (assets/theme-events.js).
        if (didWrite) {
          document.dispatchEvent(new CustomEvent('theme:update:cart'));
        }

        if (queued) {
          queued = false;
          var inheritedDrawer = queuedOpenDrawer || wantDrawer;
          queuedOpenDrawer = false;
          return reconcile({ openDrawer: inheritedDrawer });
        }

        // Only a cookie-box add opens the drawer. An add-to-cart on any other
        // product is left to the theme's own `open_modal_on_add_to_cart`.
        if (wantDrawer && sawBoxLine) {
          openCartDrawerWhenReady(didWrite);
        }
      });
  }

  document.addEventListener('theme:product:add', function () {
    reconcile({ openDrawer: true });
  });

  document.addEventListener('theme:line-item:change', function () {
    reconcile({ openDrawer: false });
  });

  /**
   * Self-heal on load, but only when Liquid has already detected an actual
   * inconsistency in the rendered cart — so normal page loads cost no extra
   * request.
   */
  if (config.needsReconcile) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        reconcile({ openDrawer: false });
      });
    } else {
      reconcile({ openDrawer: false });
    }
  }
})();
